import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Card, InputField, Text, View } from "../base";
import { Icon } from "../base/Icon";
import { sessionActions } from "../../redux/reducers/sessionSlice";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../../redux";

type UserInvite = {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string;
  role: string;
  status: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
};

/**
 * Shown when an authenticated, email-verified user has no usable organization
 * context — typically because they just left their last org, were kicked from
 * it, or signed up via OAuth without an invite. Without this screen the rest
 * of the app spins forever waiting for `currentOrganization` to be non-null.
 *
 * Two recovery paths:
 *   - Create a fresh organization (becomes their default automatically since
 *     it's their first membership server-side).
 *   - Accept any pending invites addressed to their email.
 */
export const NoOrganizationScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.session.user);

  const listInvitesRequest = useApiRequest<{ data: UserInvite[] }>();
  const acceptRequest = useApiRequest();
  const declineRequest = useApiRequest();

  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [pendingInviteId, setPendingInviteId] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const loadInvites = () => listInvitesRequest.fetch("/v1/me/invites");

  useEffect(() => {
    loadInvites();
  }, []);

  const invites = listInvitesRequest.data?.data ?? [];

  const handleCreate = async () => {
    const trimmed = orgName.trim();
    if (!trimmed) return;
    setIsCreating(true);
    setCreateError(null);
    try {
      // The thunk dispatches getSession() on success; once currentOrganization
      // is hydrated, StandardLayout flips back to the normal app shell and
      // this screen unmounts. No explicit navigate() needed.
      await dispatch(sessionActions.createOrganization(trimmed)).unwrap();
    } catch (err) {
      setCreateError((err as Error)?.message || "Could not create organization");
      setIsCreating(false);
    }
  };

  const handleAccept = async (invite: UserInvite) => {
    setPendingInviteId(invite.id);
    setInviteError(null);
    const { error } = await acceptRequest.fetch(`/v1/me/invites/${invite.id}/accept`, { method: "POST" });
    if (error) {
      setInviteError(error.message || "Could not accept invite");
      setPendingInviteId(null);
      return;
    }
    // Reload session so the new membership shows up and StandardLayout can
    // exit this screen. Reload invites so any other pending entries refresh.
    await Promise.all([dispatch(sessionActions.getSession()), loadInvites()]);
    setPendingInviteId(null);
  };

  const handleDecline = async (invite: UserInvite) => {
    setPendingInviteId(invite.id);
    setInviteError(null);
    const { error } = await declineRequest.fetch(`/v1/me/invites/${invite.id}/decline`, { method: "POST" });
    if (error) {
      setInviteError(error.message || "Could not decline invite");
      setPendingInviteId(null);
      return;
    }
    await loadInvites();
    setPendingInviteId(null);
  };

  const handleSignOut = () => {
    navigate("/signout");
  };

  const hasInvites = invites.length > 0;

  return (
    <View className="flex flex-col items-center justify-center w-full h-full px-4 py-8 overflow-y-auto">
      <Card className="flex flex-col items-center gap-4 p-8 max-w-md w-full">
        <Icon
          name="users"
          className="text-brand text-4xl mb-2"
        />
        <Text className="text-2xl font-semibold text-gray-900 dark:text-white text-center">No organization yet</Text>
        <Text className="text-sm text-gray-500 dark:text-gray-400 text-center">
          Signed in as{" "}
          <Text
            as="span"
            className="font-semibold text-gray-900 dark:text-white"
          >
            {user?.email}
          </Text>
          . Pick up where you left off by accepting an invite or creating a new organization.
        </Text>

        {hasInvites && (
          <View className="w-full mt-2 space-y-2">
            <Text className="text-xs font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">Pending invites</Text>
            <View className="flex flex-col gap-2">
              {invites.map((invite) => {
                const isPending = pendingInviteId === invite.id;
                return (
                  <View
                    key={invite.id}
                    className="flex flex-row items-center justify-between gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <View className="flex flex-col min-w-0">
                      <Text className="text-sm font-medium text-gray-900 dark:text-white truncate">{invite.organizationName}</Text>
                      <Text className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">{invite.role}</Text>
                    </View>
                    <View className="flex flex-row items-center gap-2 shrink-0">
                      <Button
                        variant="neutral"
                        size="auto"
                        onClick={() => handleDecline(invite)}
                        isDisabled={!!pendingInviteId}
                      >
                        {isPending && declineRequest.isLoading ? "Declining…" : "Decline"}
                      </Button>
                      <Button
                        variant="success"
                        size="auto"
                        onClick={() => handleAccept(invite)}
                        isDisabled={!!pendingInviteId}
                      >
                        <Icon
                          name="check"
                          size={14}
                        />
                        {isPending && acceptRequest.isLoading ? "Accepting…" : "Accept"}
                      </Button>
                    </View>
                  </View>
                );
              })}
            </View>
            {inviteError && <Text className="text-xs text-red-500 dark:text-red-400">{inviteError}</Text>}
          </View>
        )}

        <View className="w-full space-y-3 mt-2">
          {hasInvites && (
            <View className="flex flex-row items-center gap-3">
              <View className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              <Text className="text-xs uppercase tracking-wider text-gray-400 dark:text-gray-500">or</Text>
              <View className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
            </View>
          )}

          <InputField
            label="Create a new organization"
            placeholder="Acme Inc."
            value={orgName}
            onChange={(value) => {
              setOrgName(value);
              if (createError) setCreateError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreate();
            }}
            error={createError || undefined}
          />

          <Button
            onClick={handleCreate}
            isLoading={isCreating}
            isDisabled={!orgName.trim()}
            className="w-full"
          >
            Create organization
          </Button>
        </View>

        <Button
          variant="ghost"
          onClick={handleSignOut}
          className="mt-2"
        >
          <Icon
            name="logout"
            className="text-lg mr-2"
          />
          <Text>Sign out</Text>
        </Button>
      </Card>
    </View>
  );
};
